import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import bookingConfigService from "../../features/booking/bookingConfigService";
import bookingFunctionsService from "../../features/booking/bookingFunctionsService";

const formatDateKey = (date, timeZone) => {
  try {
    return new Intl.DateTimeFormat("en-CA", { timeZone }).format(date);
  } catch (error) {
    return new Intl.DateTimeFormat("en-CA").format(date);
  }
};

const PublicBooking = () => {
  const { userId } = useParams();
  const [settings, setSettings] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [slotsByDate, setSlotsByDate] = useState({});
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", notes: "" });
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadSettings = async () => {
      const data = await bookingConfigService.getBookingSettings(userId);
      setSettings(data);
    };
    loadSettings();
  }, [userId]);

  const fetchSlots = async (baseDate) => {
    if (!settings?.timezone) return;
    setLoadingSlots(true);
    const start = new Date(baseDate.getFullYear(), baseDate.getMonth(), 1);
    const end = new Date(baseDate.getFullYear(), baseDate.getMonth() + 1, 0, 23, 59, 59);

    try {
      const response = await bookingFunctionsService.getPublicSlots({
        userId,
        from: start.toISOString(),
        to: end.toISOString(),
        timezone: settings.timezone,
      });
      const map = {};
      response.days.forEach((day) => {
        map[day.date] = day.slots;
      });
      setSlotsByDate(map);
    } finally {
      setLoadingSlots(false);
    }
  };

  useEffect(() => {
    if (settings?.timezone) {
      fetchSlots(selectedDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings?.timezone]);

  const selectedDateKey = useMemo(() => {
    if (!settings?.timezone) return "";
    return formatDateKey(selectedDate, settings.timezone);
  }, [selectedDate, settings?.timezone]);

  const slotsForDate = slotsByDate[selectedDateKey] || [];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedSlot) return;
    setError(null);
    try {
      const response = await bookingFunctionsService.createPublicBooking({
        userId,
        startAt: selectedSlot.startAt,
        durationMinutes: settings.durationMinutes,
        timezone: settings.timezone,
        inviteeName: form.name,
        inviteeEmail: form.email,
        notes: form.notes,
      });
      setResult(response);
    } catch (err) {
      setError(err?.message || "Randevu oluşturulamadı.");
    }
  };

  if (!settings) {
    return <div className="p-4">Yükleniyor...</div>;
  }

  return (
    <div className="container py-5">
      <div className="row">
        <div className="col-lg-5">
          <div className="card">
            <div className="card-body">
              <h4 className="mb-2">Randevu Al</h4>
              <p className="text-muted">{settings.durationMinutes} dakika görüşme</p>
              <p className="text-muted">Saat dilimi: {settings.timezone}</p>
            </div>
          </div>
        </div>

        <div className="col-lg-7">
          <div className="card">
            <div className="card-body">
              <div className="row g-4">
                <div className="col-md-6">
                  <h5 className="mb-3">Tarih seç</h5>
                  <Calendar
                    onChange={(date) => {
                      const nextDate = Array.isArray(date) ? date[0] : date;
                      setSelectedDate(nextDate);
                      setSelectedSlot(null);
                      fetchSlots(nextDate);
                    }}
                    value={selectedDate}
                  />
                  {loadingSlots && <p className="mt-2">Uygunluk yükleniyor...</p>}
                </div>
                <div className="col-md-6">
                  <h5 className="mb-3">Saat seç</h5>
                  {slotsForDate.length === 0 && (
                    <p className="text-muted">Bu tarihte müsait saat bulunamadı.</p>
                  )}
                  <div className="d-grid gap-2">
                    {slotsForDate.map((slot) => (
                      <button
                        key={slot.startAt}
                        className={`btn ${
                          selectedSlot?.startAt === slot.startAt
                            ? "btn-primary"
                            : "btn-outline-primary"
                        }`}
                        onClick={() => setSelectedSlot(slot)}
                        type="button"
                      >
                        {slot.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {selectedSlot && (
                <form className="mt-4" onSubmit={handleSubmit}>
                  <h5>Detaylar</h5>
                  <div className="mb-3">
                    <label className="form-label">Ad Soyad</label>
                    <input
                      type="text"
                      className="form-control"
                      value={form.name}
                      onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">E-posta</label>
                    <input
                      type="email"
                      className="form-control"
                      value={form.email}
                      onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Not</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      value={form.notes}
                      onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
                    />
                  </div>
                  <button className="btn btn-primary" type="submit">
                    Randevu oluştur
                  </button>
                </form>
              )}

              {result && (
                <div className="alert alert-success mt-4">
                  {result.status === "pending"
                    ? "Randevu talebiniz alındı, onay bekleniyor."
                    : "Randevunuz oluşturuldu."}
                </div>
              )}
              {error && <div className="alert alert-danger mt-4">{error}</div>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicBooking;
